import { TFunction } from 'i18next'

import {
  TIME_06_00,
  TIME_12_00,
  TIME_18_00,
  TIME_23_00,
} from 'pages/avia/filter/filter-time-screen/components/time-checkbox-group'

import {
  ArrDepTime,
  DataForFilter,
  FilterTicketForm,
} from 'entities/ticket/models/find-ticket/models'

import { Carrier } from 'shared/models/ticket'

const HOURS_IN_MINUTES = 60
const MAX_HOURS_IN_DAY = 24
const MILLION = 1000000
const THOUSAND = 1000

interface GetFilterDisplayTextProps {
  filter: FilterTicketForm | undefined
  filterDataFromRequest?: DataForFilter | null
  t: TFunction
}

export const getFilterDisplayText = ({
  filter,
  filterDataFromRequest,
  t,
}: GetFilterDisplayTextProps) => {
  return {
    airlines: formatAirlinesText(
      filter?.airlines,
      filterDataFromRequest?.airlines,
    ),
    transfers: formatTransfersText(filter, t),
    price: formatPriceText(filter?.rangePrice, t),
    time: formatTimeText(filter?.time, t),
  }
}

const getAirlineTitle = (
  airline: Carrier,
  airlinesFromRequest: Carrier[] | undefined,
): string => {
  if (airline.title && airline.title !== airline.code) {
    return airline.title
  }
  const found = airlinesFromRequest?.find((a) => a.code === airline.code)
  return found?.title ?? airline.title
}

const formatAirlinesText = (
  airlines: Carrier[] | undefined,
  airlinesFromRequest?: Carrier[],
): string => {
  if (!airlines?.length) return ''

  const firstTitle = getAirlineTitle(airlines[0], airlinesFromRequest)

  if (airlines.length === 1) {
    return firstTitle
  }

  const othersCount = airlines.length - 1
  return `${firstTitle} +${othersCount}`
}

const formatDurationText = (
  minDuration: number,
  maxDuration: number,
  t: TFunction,
): string => {
  const minHours = Math.floor(minDuration / HOURS_IN_MINUTES)
  const maxHours = Math.floor(maxDuration / HOURS_IN_MINUTES)

  if (minHours === 0 && maxHours === MAX_HOURS_IN_DAY) {
    return ''
  }

  if (minHours === 0) {
    return `${t('range_to')} ${maxHours} ${t('hours_short')}`
  }

  return `${t('range_from')} ${minHours} ${t('range_to')} ${maxHours} ${t('hours_short')}`
}

const collectTransferConditions = (
  filter: FilterTicketForm | undefined,
  t: TFunction,
): string[] => {
  const conditions: string[] = []

  if (filter?.isWithRepeatRegistration) {
    conditions.push(t('without_reregistration'))
  }

  if (filter?.isWithNightTransition) {
    conditions.push(t('without_night_transfer'))
  }

  if (filter?.isWithChangeAirport) {
    conditions.push(t('without_change_airport'))
  }

  return conditions
}

const formatTransfersText = (
  filter: FilterTicketForm | undefined,
  t: TFunction,
): string => {
  const parts: string[] = []

  if (filter?.durationOfTransfers) {
    const durationText = formatDurationText(
      filter.durationOfTransfers.min_duration,
      filter.durationOfTransfers.max_duration,
      t,
    )
    if (durationText) {
      parts.push(durationText)
    }
  }

  const conditions = collectTransferConditions(filter, t)
  parts.push(...conditions)

  if (parts.length === 0) {
    return ''
  }

  if (parts.length === 1) {
    return parts[0]
  }

  const othersCount = parts.length - 1
  return `${parts[0]} +${othersCount}`
}

const formatSinglePrice = (price: number, t: TFunction): string => {
  if (price >= MILLION) {
    return `${Math.round(price / MILLION)} ${t('million_short')}`
  }

  if (price >= THOUSAND) {
    return `${Math.round(price / THOUSAND)} ${t('thousand_short')}`
  }

  return price.toString()
}

const formatPriceText = (
  rangePrice: { min: number; max: number } | undefined,
  t: TFunction,
): string => {
  if (!rangePrice) return ''

  const minText = formatSinglePrice(rangePrice.min, t)
  const maxText = formatSinglePrice(rangePrice.max, t)

  return `${t('range_from')} ${minText} ${t('range_to')} ${maxText}`
}

const detectTimePeriod = (from: number, to: number): string | null => {
  if (from === TIME_06_00 && to === TIME_12_00) return 'morning'

  if (from === TIME_12_00 && to === TIME_18_00) return 'day'

  if (from === TIME_18_00 && to === TIME_23_00) return 'evening'

  if (from === TIME_23_00 && to === TIME_06_00) return 'night'

  return null
}

const formatTimeText = (
  time: ArrDepTime[] | undefined,
  t: TFunction,
): string => {
  if (!time || time.length === 0) return ''

  const allTimeRanges: Array<{ from: number; to: number }> = []

  time.forEach((item) => {
    const departures = item.departure
      ? Array.isArray(item.departure)
        ? item.departure
        : [item.departure]
      : []

    const arrivals = item.arrival
      ? Array.isArray(item.arrival)
        ? item.arrival
        : [item.arrival]
      : []

    ;[...departures, ...arrivals].forEach((range) => {
      if (range.from || range.to) {
        allTimeRanges.push({
          from: Number(range.from),
          to: Number(range.to),
        })
      }
    })
  })

  if (allTimeRanges.length === 0) return ''

  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / HOURS_IN_MINUTES)
    const mins = minutes % HOURS_IN_MINUTES
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
  }

  const firstRange = allTimeRanges[0]
  const period = detectTimePeriod(firstRange.from, firstRange.to)

  if (period) {
    if (allTimeRanges.length === 1) {
      return t(`departure_${period}`)
    }
    const othersCount = allTimeRanges.length - 1
    return `${t(`departure_${period}`)} +${othersCount}`
  }

  if (allTimeRanges.length === 1) {
    return `${formatMinutes(firstRange.from)}-${formatMinutes(firstRange.to)}`
  }

  return `${formatMinutes(firstRange.from)}-${formatMinutes(firstRange.to)} +${allTimeRanges.length - 1}`
}
